import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import {
  dataApiClient,
  type AgentAggregationMetric,
  type TripAggregationMetric,
  type AgentAggregationDimension,
  type TripAggregationDimension,
} from '../../infrastructure/dataApi/client';
import type { Aggregation } from '../../types/domain';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useMapVisibility } from '../SimViewer/useMapVisibility';
import {
  useAggregationConfig,
  type AggregationTarget,
  type AggregationSubTarget,
} from '../SimViewer/useAggregationConfig';
import { includes } from '../../utils/array';

type AreaAggregationDimension = Extract<
  AgentAggregationDimension | TripAggregationDimension,
  'home' | 'work' | 'start' | 'end'
>;

type UseAreaAggregationData = {
  isLoading: boolean;
  aggregations: Map<string, number>;
};

const EMPTY_DATA = new Map<string, never>();

const getDimension = (
  target?: AggregationTarget,
  subTarget?: AggregationSubTarget
): AreaAggregationDimension | null => {
  if (includes(['home', 'work', 'start', 'end'], target)) {
    return target;
  }
  if (
    includes(['traveled_distance', 'traveled_time', 'activity_time'], target)
  ) {
    return subTarget ?? null;
  }
  return null;
};

const useAreaAggregationData = (
  metric?: AgentAggregationMetric | TripAggregationMetric,
  dimension?: AreaAggregationDimension
): UseAreaAggregationData => {
  const { visibleAreaType } = useMapVisibility();
  const { currentSimDataId } = useCurrentSimDataId();
  const { apiFilter } = useApiDataFilter();
  const { aggregationConfig } = useAggregationConfig();

  const aggregationDimension =
    dimension ??
    getDimension(aggregationConfig?.target, aggregationConfig?.subTarget);
  const aggregationMetric = metric ?? aggregationConfig?.metric ?? null;
  const areaType =
    aggregationConfig?.type === 'area_pref' ? 'prefecture' : visibleAreaType;
  const isRequired =
    aggregationDimension !== null &&
    aggregationMetric !== null &&
    (!!dimension || includes(['areas', 'area_pref'], aggregationConfig?.type));

  const { data, isFetching } = useQuery({
    queryKey: [
      'areaAggregation',
      currentSimDataId,
      apiFilter,
      aggregationMetric,
      aggregationDimension,
      areaType,
    ],
    queryFn:
      currentSimDataId && isRequired
        ? async ({
            signal,
          }: QueryFunctionContext): Promise<Map<string, number>> => {
            if (
              includes(['count', 'count_per_area'], aggregationMetric) &&
              includes(['home', 'work'], aggregationDimension)
            ) {
              return dataApiClient
                .getAgentAggr(
                  currentSimDataId,
                  apiFilter,
                  aggregationMetric,
                  aggregationDimension,
                  areaType,
                  signal
                )
                .then((items: Aggregation[]): Map<string, number> => {
                  return new Map<string, number>(
                    items.map((item: Aggregation): [string, number] => {
                      return [item.key, item.value];
                    })
                  );
                });
            } else {
              return dataApiClient
                .getTripAggr(
                  currentSimDataId,
                  apiFilter,
                  aggregationMetric,
                  aggregationDimension,
                  areaType,
                  signal
                )
                .then((items: Aggregation[]): Map<string, number> => {
                  return new Map<string, number>(
                    items.map((item: Aggregation): [string, number] => {
                      return [item.key, item.value];
                    })
                  );
                });
            }
          }
        : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: dimension ? 'others' : 'aggregation' },
  });

  return {
    isLoading: isFetching,
    aggregations: data && isRequired ? data : EMPTY_DATA,
  };
};

export { useAreaAggregationData };
